import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const PythonOperatorsArithmetic = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Python Operators</div>
                <h1>Arithmetic Operators & Internals</h1>
                <p className="hero-lead">
                    Go beyond basic math. Discover how Python's arithmetic actually works under the hood,
                    from operator overloading via Magic Methods to the binary complexities of floating-point precision.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    On the surface, Python adds numbers just like a calculator. `2 + 2` is `4`. Simple.
                </p>
                <p>
                    But underneath, Python is doing something remarkable. When you execute <code>a + b</code>, Python doesn't just "add" them.
                    It checks the types of <code>a</code> and <code>b</code>, looks for a special method called <code>__add__</code>,
                    and executes arbitrary code defined by that type. This architecture<strong>Operator Overloading</strong>is what allows
                    you to "add" two strings together (<code>"hello" + "world"</code>) or "multiply" a list (<code>[1] * 5</code>) with the exact same syntax.
                </p>
                <p>
                    In this deep dive, we will explore the seven arithmetic operators, the "True Division" change that broke the internet
                    during the Python 2-to-3 transition, and the infinite precision of Python integers.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>Operator Internals:</strong> How `+` blindly calls `__add__()`.</li>
                        <li><strong>Precision Pitfalls:</strong> Why floating point math is "broken" and how to fix it.</li>
                        <li><strong>The Modulo Quirk:</strong> Why Python's `%` acts differently than C or Java with negative numbers.</li>
                        <li><strong>Power Features:</strong> Using `pow(x, y, z)` for cryptography-grade speed.</li>
                        <li><strong>Precedence Rules:</strong> Mastering PEMDAS in complex expressions.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Seven Arithmetic Operators</h2>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Symbol</th>
                                <th>Operation</th>
                                <th>Example</th>
                                <th>Result</th>
                                <th>Dunder Method</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>+</code></td>
                                <td>Addition</td>
                                <td><code>10 + 2</code></td>
                                <td><code>12</code></td>
                                <td><code>__add__</code></td>
                            </tr>
                            <tr>
                                <td><code>-</code></td>
                                <td>Subtraction</td>
                                <td><code>10 - 2</code></td>
                                <td><code>8</code></td>
                                <td><code>__sub__</code></td>
                            </tr>
                            <tr>
                                <td><code>*</code></td>
                                <td>Multiplication</td>
                                <td><code>10 * 2</code></td>
                                <td><code>20</code></td>
                                <td><code>__mul__</code></td>
                            </tr>
                            <tr>
                                <td><code>/</code></td>
                                <td>True Division</td>
                                <td><code>10 / 2</code></td>
                                <td><code>5.0</code></td>
                                <td><code>__truediv__</code></td>
                            </tr>
                            <tr>
                                <td><code>//</code></td>
                                <td>Floor Division</td>
                                <td><code>10 // 3</code></td>
                                <td><code>3</code></td>
                                <td><code>__floordiv__</code></td>
                            </tr>
                            <tr>
                                <td><code>%</code></td>
                                <td>Modulo</td>
                                <td><code>10 % 3</code></td>
                                <td><code>1</code></td>
                                <td><code>__mod__</code></td>
                            </tr>
                            <tr>
                                <td><code>**</code></td>
                                <td>Exponentiation</td>
                                <td><code>2 ** 3</code></td>
                                <td><code>8</code></td>
                                <td><code>__pow__</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Deep Dive: Operator Overloading</h2>
                <p>
                    Everything in Pythonnumbers, strings, listsis an object. When you use an operator, you are actually calling a method on that object.
                    This meant you can define how operators behave for your own custom classes.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Writing a Vector Class with Math Support</span>
                    </div>
                    <CodeBlock language="python" code={`class Vector:
    def __init__(self, x, y):
        self.x = x
        self.y = y

    # Overload the '+' operator
    def __add__(self, other):
        return Vector(self.x + other.x, self.y + other.y)

    # Overload string representation for printing
    def __repr__(self):
        return f"Vector({self.x}, {self.y})"

v1 = Vector(2, 4)
v2 = Vector(1, 1)

# Python converts this to: v1.__add__(v2)
v3 = v1 + v2

print(v3)  # Vector(3, 5)`} />
                </div>
                <h3>Why this matters</h3>
                <p>
                    This "Data Model" is the secret sauce behind libraries like NumPy and Pandas. When you add two NumPy arrays,
                    they don't loop through elements slowly in Python. They use the overloaded <code>__add__</code> method to dispatch
                    execution to highly optimized C code, making it thousands of times faster.
                </p>
            </div>

            <div className="content-section">
                <h2>The Magic Method Trilogy: __add__, __radd__, __iadd__</h2>
                <p>
                    Implementing <code>__add__</code> is just the beginning. To make your custom objects truly behave like numbers (e.g., to support <code>10 + vector</code> vs <code>vector + 10</code>),
                    you need to understand the full protocol.
                </p>

                <h3>1. Forward Addition (<code>__add__</code>)</h3>
                <p>
                    Called when your object is on the LEFT side of the <code>+</code>. (e.g., <code>vector + 10</code> calls <code>vector.__add__(10)</code>).
                </p>

                <h3>2. Reverse Addition (<code>__radd__</code>)</h3>
                <p>
                    What happens if you do <code>10 + vector</code>? The integer <code>10</code> doesn't know how to add a Vector!
                    Python sees that <code>int.__add__</code> returns <code>NotImplemented</code>, so it flips the operation and calls
                    <code>vector.__radd__(10)</code>. This allows your custom objects to play nicely with built-in types.
                </p>

                <h3>3. In-Place Addition (<code>__iadd__</code>)</h3>
                <p>
                    Called when you write <code>vector += 10</code>. This is critical for performance.
                    If you don't implement this, Python just does <code>x = x + y</code> (creating a new object).
                    For strict performance (like with big lists), <code>__iadd__</code> should modify the object <em>in place</em> (return <code>self</code>)
                    instead of creating a new one.
                </p>
            </div>

            <div className="content-section">
                <h2>The Forgotten Operator: Unary Invert (~)</h2>
                <p>
                    Most Python courses skip the tilde (<code>~</code>), but it's crucial for indexing and bitwise interactions.
                    It performs a <strong>Bitwise NOT</strong>, which inverts all bits of the number.
                </p>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>The Formula:</strong> For any integer <code>x</code>, <code>~x</code> is equivalent to <code>-(x + 1)</code>.
                    </div></ProTip>
                <CodeBlock language="python" code={`x = 5   # Binary:  ...000101
inv = ~x # Binary: ...1111010 (Two's manual complement notation)

print(inv) # -6

# Why is this useful?
# It's perfect for symmetric indexing!
data = ['a', 'b', 'c', 'd', 'e']

print(data[0])   # 'a' (First)
print(data[~0])  # 'e' (Last)  -&gt; data[-1]
print(data[1])   # 'b' (Second)
print(data[~1])  # 'd' (Second to last) -&gt; data[-2]`} />
            </div>

            <div className="content-section">
                <h2>Complex Numbers & Math Modules</h2>
                <p>
                    Python is one of the distinct few languages with native support for Complex Numbers.
                    Engineers use them for electrical engineering (AC circuits), signal processing (Fourier Transforms), and quantum computing.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Working with Complex Numbers</span>
                    </div>
                    <CodeBlock language="python" code={`# Syntax: real + imaginary 'j'
# Note: Mathematicians use 'i', but Engineers use 'j'. Python uses 'j'.

z1 = 2 + 3j
z2 = 1 - 1j

print(z1 + z2)      # (3+2j)
print(z1 * z2)      # (5+1j)

# Getting components
print(z1.real)      # 2.0
print(z1.imag)      # 3.0
print(abs(z1))      # 3.605... (Magnitude)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Performance Optimization: divmod()</h2>
                <p>
                    Often in algorithms (like converting seconds to hours/minutes/seconds), you need both the quotient (<code>//</code>)
                    and the remainder (<code>%</code>). Calculating them separately requires two division operations.
                </p>
                <p>
                    Python provides <code>divmod(a, b)</code> which returns a tuple <code>(quotient, remainder)</code> in one step.
                    At the C level, this maps to a single CPU instruction on many architectures, making it twice as fast.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Using divmod()</span>
                    </div>
                    <CodeBlock language="python" code={`total_seconds = 3665

# The slow way
hours = total_seconds // 3600
remainder = total_seconds % 3600

# The fast way
hours, remainder = divmod(total_seconds, 3600)
minutes, seconds = divmod(remainder, 60)

print(f"{hours}h {minutes}m {seconds}s")`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Floating Point Nightmare</h2>
                <p>
                    Every programmer eventually encounters this confusing behavior. You expect math to be exact, but computers disagree.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>0.1 + 0.2 != 0.3</span>
                    </div>
                    <CodeBlock language="python" code={`val = 0.1 + 0.2
print(val) 
# Output: 0.30000000000000004 

print(val == 0.3)
# Output: False`} />
                </div>

                <h3>What is happening?</h3>
                <p>
                    This is NOT a Python bug. It is a limitation of hardware. Computers interpret numbers in Base-2 (Binary).
                    Just like <code>1/3</code> cannot be represented exactly in Base-10 (0.3333...), <code>0.1</code> cannot be represented exactly in Base-2.
                    It becomes an infinitely repeating binary fraction logic.
                </p>
                <p>
                    When you add the slightly imprecise binary representation of 0.1 to 0.2, the tiny errors accumulate to form that <code>...0004</code> at the end.
                </p>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>The Fix:</strong> Never compare floats with <code>==</code>. Use <code>math.isclose()</code> for robust comparisons.
                    </div></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>Correct Comparison</span>
                    </div>
                    <CodeBlock language="python" code={`import math

a = 0.1 + 0.2
b = 0.3

# Check if they are "close enough" (within distinct tolerance)
if math.isclose(a, b):
    print("They match!")  # This prints!`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Special Values: Infinity and NaN</h2>
                <p>
                    Floating point numbers have special states for "Infinity" and "Not a Number".
                    These are valid float values and follow their own mathematical logic.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Handling Infinite Values</span>
                    </div>
                    <CodeBlock language="python" code={`# Creating Infinity
pos_inf = float('inf')
neg_inf = float('-inf')

print(pos_inf &gt; 1000000000)  # True
print(pos_inf + 100)         # inf (Infinity eats additions)
print(pos_inf * 2)           # inf

# NaN (Not a Number)
impossible = float('nan')
print(impossible == impossible)  # False! NaN is never equal to itself.
print(math.isnan(impossible))    # True (The only way to check)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Exact Math: The Fraction Module</h2>
                <p>
                    If you hate floating point errors and need perfect precision (like in specialized engineering or music theory applications),
                    Python's <code>fractions</code> module allows you to do math with rational numbers.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Rational Arithmetic</span>
                    </div>
                    <CodeBlock language="python" code={`from fractions import Fraction

f1 = Fraction(1, 3)  # 1/3
f2 = Fraction(1, 2)  # 1/2

result = f1 + f2
print(result)        # 5/6 (Perfectly exact!)
print(float(result)) # 0.833333...

# Solving the 0.1 issue
exact_val = Fraction('0.1') + Fraction('0.2')
print(exact_val)     # 3/10`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Modulo: The Negative Number Trap</h2>
                <p>
                    The Modulo operator (<code>%</code>) returns the remainder of division.
                    However, different programming languages handle negative numbers differently.
                </p>
                <p>
                    Python's rule: <strong>The result of the modulo always has the same sign as the divisor.</strong>
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Python vs C Behavior</span>
                    </div>
                    <CodeBlock language="python" code={`# Positive is simple
print(10 % 3)    # 1

# Negative Operand
print(-10 % 3)   # 2 (Result is positive, like divisor 3)
print(10 % -3)   # -2 (Result is negative, like divisor -3)

# In C or Java, -10 % 3 would be -1.
# This makes Python strictly mathematically correct for "clock arithmetic".`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Advanced Power: Modular Exponentiation</h2>
                <p>
                    Calculating massive powers is computationally expensive.
                    If you try to calculate <code>base ** exp</code> where numbers have hundreds of digits, your RAM will explode.
                </p>
                <p>
                    However, in cryptography (like RSA algorithms), we constantly need to calculate <code>(base ** exp) % mod</code>.
                    Python provides a built-in function for this that is insanely optimized: <code>pow(base, exp, mod)</code>.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The 3-Argument pow()</span>
                    </div>
                    <CodeBlock language="python" code={`base = 123456789
exp = 987654321
mod = 1000000007  # Standard prime for competitive programming

#  The Slow Way
# result = (base ** exp) % mod
# This first calculates a number with MILLIONS of digits, then modulo. Too slow.

# The Fast Way
result = pow(base, exp, mod)
# This applies modulo at every multiplication step, keeping numbers small.
# It runs in milliseconds.`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Matrix Multiplication Operator (@)</h2>
                <p>
                    Introduced in Python 3.5 (PEP 465), the <code>@</code> operator is unique.
                    It is the only arithmetic operator dedicated to Linear Algebra.
                    Unlike <code>*</code> which performs element-wise multiplication (like <code>[1, 2] * [2, 3] = [2, 6]</code> in NumPy),
                    <code>@</code> performs dot products or matrix-matrix multiplication.
                </p>
                <p>
                    While vanilla Python lists don't support it by default, understanding it is mandatory for Data Science.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Implementing @ (Matrix Mult)</span>
                    </div>
                    <CodeBlock language="python" code={`class Matrix:
    def __init__(self, value):
        self.value = value

    def __matmul__(self, other):
        # This is where you'd implement dot product math
        return f"Multiplying Matrix({self.value}) with Matrix({other.value})"

A = Matrix(5)
B = Matrix(10)

print(A @ B)  # Calls A.__matmul__(B)
# Output: "Multiplying Matrix(5) with Matrix(10)"`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Augmented Assignment Trap (+=)</h2>
                <p>
                    Operators like <code>+=</code>, <code>-=</code>, and <code>*=</code> are called "Augmented Assignment".
                    They look like syntax sugar for <code>x = x + 1</code>, but they behave differently for Mutable vs Immutable objects.
                </p>

                <h3>1. Immutable (Integers, Strings, Tuples)</h3>
                <p>
                    For immutable types, <code>x += y</code> is exactly <code>x = x + y</code>.
                    Python calculates the result, creates a NEW object, and rebinds the variable <code>x</code> to it.
                </p>

                <h3>2. Mutable (Lists, Dictionaries)</h3>
                <p>
                    For mutable types, <code>x += y</code> calls <code>__iadd__</code> (In-Place Add).
                    The list modifies ITSELF. The memory address (<code>id(x)</code>) stays the same.
                    This distinction causes the infamous "Tuple Trap".
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Tuple of Lists Trap</span>
                    </div>
                    <CodeBlock language="python" code={`# A tuple (immutable) containing a list (mutable)
t = ([1, 2], [3, 4])

# Try to modify the list inside the tuple
try:
    t[0] += [3]
except TypeError as e:
    print(f"Error: {e}") 
    # Tuple complains: "tuple object does not support item assignment"

# BUT LOOK AT THE DATA!
print(t)  # ([1, 2, 3], [3, 4]) 
# The modification happened (!), THEN the assignment failed.

# Why?
# 1. t[0] accessed the list.
# 2. list += [3] modified the list in-place (Success).
# 3. Python tried to assign the result BACK to t[0] (t[0] = result).
# 4. Tuple said "No assignment allowed!".`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Rounding Rumble: Integers from Floats</h2>
                <p>
                    Converting a float like <code>3.7</code> to an integer seems simple, but there are four distinct ways to do it in Python,
                    and picking the wrong one causes financial bugs.
                </p>

                <h3>1. Truncation (`int()`)</h3>
                <p>
                    Simply chops off the decimal part. Moves towards zero.
                </p>

                <h3>2. Floor (`math.floor()`)</h3>
                <p>
                    Moves towards negative infinity. (Crucial for negative numbers!).
                </p>

                <h3>3. Ceiling (`math.ceil()`)</h3>
                <p>
                    Moves towards positive infinity.
                </p>

                <h3>4. Rounding (`round()`) - The Danger Zone </h3>
                <p>
                    Python 3 uses "Bankers Rounding" (Round Half to Even).
                    <code>round(0.5)</code> is <code>0</code>, but <code>round(1.5)</code> is <code>2</code>.
                    This minimizes cumulative error in large sums, but confuses everyone else.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Comparison of Rounding Methods</span>
                    </div>
                    <CodeBlock language="python" code={`import math

val = 3.7
neg = -3.7

# 1. int() - Truncate
print(int(val))      # 3
print(int(neg))      # -3 (Note: different direction than floor!)

# 2. floor() - Down
print(math.floor(val)) # 3
print(math.floor(neg)) # -4 (Lower than -3.7)

# 3. ceil() - Up
print(math.ceil(val))  # 4
print(math.ceil(neg))  # -3

# 4. round() - Bankers Rounding
print(round(2.5))    # 2 (Nearest Even!)
print(round(3.5))    # 4 (Nearest Even!)
print(round(2.6))    # 3 (Standard)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The `math` Module Powerhouse</h2>
                <p>
                    Python's built-in operators are just the tip of the iceberg. For serious calculations, the standard library <code>math</code> module
                    offers optimized C implementations of common algorithms. Using these is not just about convenience; it's about correctness.
                </p>

                <h3>1. Precision Summation (`math.fsum`)</h3>
                <p>
                    Adding many floats together accumulates error. `sum()` is fast but naive. `math.fsum()` tracks partial sums to maintain precision.
                </p>
                <CodeBlock language="python" code={`# The floating point error accumulation
values = [0.1] * 10
print(sum(values))        # 0.9999999999999999 
print(math.fsum(values))  # 1.0 `} />

                <h3>2. Integer Square Root (`math.isqrt`)</h3>
                <p>
                    New in Python 3.8. If you need the integer part of a square root, don't use <code>int(math.sqrt(x))</code>.
                    It converts to float and back, losing precision for huge numbers. `isqrt` stays in integer land.
                </p>

                <h3>3. Number Theory (`gcd`, `lcm`)</h3>
                <p>
                    Essential for cryptographic algorithms and fraction math.
                </p>
                <CodeBlock language="python" code={`print(math.gcd(8, 12))  # 4
print(math.lcm(8, 12))  # 24 (Python 3.9+)`} />

                <h3>4. Useful Constants</h3>
                <p>
                    Never type `3.14159` manually.
                </p>
                <CodeBlock language="python" code={`print(math.pi)   # 3.141592653589793
print(math.e)    # 2.718281828459045
print(math.tau)  # 6.28318... (2 * pi)`} />
            </div>

            <div className="content-section">
                <h2>Performance Lab: Bitwise vs Arithmetic</h2>
                <p>
                    A common optimization trick in C/C++ is to use Bitwise Left Shift (<code>&lt;&lt;</code>) instead of multiplication by 2.
                    Does this hold true in Python?
                </p>
                <CodeBlock language="python" code={`import timeit

# Multiplying by 2
print(timeit.timeit('x = 100 * 2', number=10000000))
# Output: ~0.11 seconds

# Shifting left by 1 (Equivalent to *2)
print(timeit.timeit('x = 100 << 1', number=10000000))
# Output: ~0.11 seconds

# Verdict: No significant difference!`} />
                <p>
                    <strong>Why?</strong> Python's overhead interpretation cost dwarfs the CPU cycle difference between <code>MUL</code> and <code>SHL</code> instructions.
                    However, for <strong>massive integers</strong> (thousands of digits), bitwise operations can be significantly faster because they operate directly on the underlying bits without complex carry-over logic.
                </p>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Takeaway:</strong> Write readable code (<code>* 2</code>) unless you are doing low-level bit manipulation or optimizing a tight loop with massive numbers.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Operator Precedence & Associativity Chart</h2>
                <p>
                    When you write <code>2 + 3 * 4 ** 2</code>, Python essentially sees a tree structure.
                    Knowing the "Binding Power" of each operator is the only way to predict the result.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Precedence</th>
                                <th>Operator</th>
                                <th>Description</th>
                                <th>Associativity</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Highest</strong></td>
                                <td><code>( ... )</code></td>
                                <td>Parentheses</td>
                                <td>-</td>
                            </tr>
                            <tr>
                                <td>2</td>
                                <td><code>**</code></td>
                                <td>Exponentiation</td>
                                <td><strong>Right-to-Left</strong> </td>
                            </tr>
                            <tr>
                                <td>3</td>
                                <td><code>+x</code>, <code>-x</code>, <code>~x</code></td>
                                <td>Unary Plus/Minus/Bitwise NOT</td>
                                <td>Right-to-Left</td>
                            </tr>
                            <tr>
                                <td>4</td>
                                <td><code>*</code>, <code>@</code>, <code>/</code>, <code>//</code>, <code>%</code></td>
                                <td>Multiplication, Matrix, Division</td>
                                <td>Left-to-Right</td>
                            </tr>
                            <tr>
                                <td>5</td>
                                <td><code>+</code>, <code>-</code></td>
                                <td>Addition, Subtraction</td>
                                <td>Left-to-Right</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Warning><strong> Critical Warning:</strong> Note that <code>**</code> is Right-Associative!
                    <code>2 ** 3 ** 2</code> is calculated as <code>2 ** (3 ** 2)</code> -&gt; <code>2 ** 9</code> -&gt; <code>512</code>.
                    If it were left-associative, it would be <code>(2 ** 3) ** 2</code> -&gt; <code>8 ** 2</code> -&gt; <code>64</code>.</Warning>

                <div className="pitfall-card" style={{ marginTop: '20px' }}>
                    <h3> The Lambda Precedence Trap</h3>
                    <p>
                        Lambda functions have the <strong>lowest</strong> binding power. They swallow everything to their right.
                    </p>
                    <CodeBlock language="python" code={`# Goal: A function that returns x + 1, then multiply result by 5
#  WRONG
f = lambda x: x + 1 * 5
print(f(2))  # 7  (Evaluated as x + (1*5) -&gt; 2 + 5)

# CORRECT
f = lambda x: (x + 1) * 5
print(f(2))  # 15 (Evaluated as (2+1) * 5)`} />
                </div>
            </div>

            <div className="content-section real-world-section">
                <h2>Real-World Applications</h2>

                <div className="use-case-grid">
                    <div className="use-case-card">
                        <h3>1. Circular Arrays (Ring Buffers)</h3>
                        <p>When you reach the end of a list, you want to wrap back to the start.</p>
                        <CodeBlock language="python" code={`colors = ["red", "green", "blue"]
index = 0

def next_color():
    global index
    # (0+1)%3=1, (1+1)%3=2, (2+1)%3=0
    index = (index + 1) % len(colors)
    return colors[index]`} />
                    </div>

                    <div className="use-case-card">
                        <h3>2. Checking Parity</h3>
                        <p>Check if a number is even or odd using modulo 2.</p>
                        <CodeBlock language="python" code={`def process(n):
    if n % 2 == 0:
        print(f"{n} is Even")
    else:
        print(f"{n} is Odd")`} />
                    </div>
                </div>
            </div>

            <div className="content-section best-practices">
                <h2>Best Practices & Common Pitfalls</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use <code>//</code> for integer division (especially for indices).</li>
                            <li>Use <code>math.isclose()</code> for float comparisons.</li>
                            <li>Use parentheses to force precedence order.</li>
                            <li>Use <code>pow(b, e, m)</code> for large number math.</li>
                        </ul>
                    </div>
                    <div className="practice-card">
                        <h3> Don't</h3>
                        <ul>
                            <li>Don't assume <code>/</code> returns an integer (Python 2 habit).</li>
                            <li>Don't compare <code>0.1 + 0.2 == 0.3</code>.</li>
                            <li>Don't blindly trust PEMDAS; be explicit.</li>
                            <li>Don't write <code>x = x + 1</code> if <code>x += 1</code> is clearer.</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div >
    );
};

export default PythonOperatorsArithmetic;


